import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { Link } from "react-router-dom";
import { 
  Rocket, 
  User, 
  Users, 
  UserPlus, 
  CheckCircle2, 
  Circle,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { cn } from "@/lib/utils";

interface ChecklistItem {
  id: string;
  title: string;
  description: string;
  icon: React.ElementType;
  href: string;
  ctaLabel: string;
  completed: boolean;
}

/**
 * Simplified 3-item checklist for new users.
 * Items: Complete Profile, Join a Team, Invite Someone
 */
const GetStartedChecklist: React.FC = () => {
  const [isCollapsed, setIsCollapsed] = useState(false);

  const { data: userData } = useQuery({
    queryKey: ["current-user-for-checklist"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      return user;
    },
  });

  const { data: checklistData, isLoading } = useQuery({
    queryKey: ["get-started-checklist", userData?.id],
    queryFn: async () => {
      if (!userData?.id) return null;

      const [profileResult, teamResult, inviteResult] = await Promise.all([
        // Check profile completeness
        supabase
          .from("contributor_profiles")
          .select("completed_onboarding, display_name")
          .eq("id", userData.id)
          .maybeSingle(),
        
        // Check team membership
        supabase
          .from("challenge_team_members")
          .select("id", { count: "exact", head: true })
          .eq("user_id", userData.id),
        
        // Check invites sent
        supabase
          .from("invite_logs")
          .select("id", { count: "exact", head: true })
          .eq("sent_by", userData.id),
      ]);

      return {
        hasProfile: !!profileResult.data?.display_name,
        hasTeam: (teamResult.count || 0) >= 1,
        hasSentInvite: (inviteResult.count || 0) >= 1,
      };
    },
    enabled: !!userData?.id,
  });

  if (isLoading) {
    return (
      <Card className="mb-6 border-primary/20 bg-gradient-to-br from-primary/5 to-transparent">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Rocket className="h-5 w-5 text-primary" />
            <Skeleton className="h-5 w-32" />
          </div>
          <Skeleton className="h-1.5 mt-2 w-full" />
        </CardHeader>
        <CardContent className="pt-0 space-y-2">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </CardContent>
      </Card>
    );
  }

  if (!checklistData) return null;

  const items: ChecklistItem[] = [
    {
      id: "profile",
      title: "Complete your profile",
      description: "Help us match you with the right opportunities",
      icon: User,
      href: "/dashboard/profile",
      ctaLabel: "Complete Profile",
      completed: checklistData.hasProfile,
    },
    {
      id: "team",
      title: "Join a challenge team",
      description: "Work with others to solve real problems",
      icon: Users,
      href: "/challenges",
      ctaLabel: "Find a Team",
      completed: checklistData.hasTeam,
    },
    {
      id: "invite",
      title: "Invite a friend",
      description: "Grow our community of builders",
      icon: UserPlus,
      href: "/dashboard/invites",
      ctaLabel: "Send an Invite",
      completed: checklistData.hasSentInvite,
    },
  ];

  const completedCount = items.filter(item => item.completed).length;
  const progress = Math.round((completedCount / items.length) * 100);

  // Hide if all complete
  if (completedCount === items.length) {
    return null;
  }

  return (
    <Card className="mb-6 border-primary/20 bg-gradient-to-br from-primary/5 to-transparent">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Rocket className="h-5 w-5 text-primary" />
            <CardTitle className="text-base">Get Started</CardTitle>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">
              {completedCount}/{items.length}
            </span>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => setIsCollapsed(!isCollapsed)}
            >
              {isCollapsed ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronUp className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>
        <Progress value={progress} className="h-1.5 mt-2" />
      </CardHeader>
      
      {!isCollapsed && (
        <CardContent className="pt-0 space-y-2">
          {items.map((item) => (
            <div
              key={item.id}
              className={cn(
                "flex items-center gap-3 p-3 rounded-lg transition-colors",
                item.completed 
                  ? "bg-muted/30" 
                  : "bg-background border border-border hover:border-primary/30"
              )}
            >
              <div className={cn(
                "shrink-0",
                item.completed ? "text-primary" : "text-muted-foreground"
              )}>
                {item.completed ? (
                  <CheckCircle2 className="h-5 w-5" />
                ) : (
                  <Circle className="h-5 w-5" />
                )}
              </div>
              
              <div className="flex-1 min-w-0">
                <span className={cn(
                  "font-medium text-sm block",
                  item.completed && "text-muted-foreground line-through"
                )}>
                  {item.title}
                </span>
              </div>
              
              {!item.completed && (
                <Button size="sm" variant="outline" asChild className="shrink-0">
                  <Link to={item.href}>
                    {item.ctaLabel}
                  </Link>
                </Button>
              )}
            </div>
          ))}
        </CardContent>
      )}
    </Card>
  );
};

export default GetStartedChecklist;
